import { errorBox } from '@core/components/AlertBox';
import { timestampToIsoString } from '@core/logic/helper/timeFormatting';
import { deviceManagementApiV1 as deviceManagementApi, photoApiV1 } from '@deepup/apis';
import { Photo, GeoPositionSolution } from '@photos/types';
import { iphotoContentClassificationApiV1 } from '@deepup/internal-apis';
import type { ScanFilters } from '@projects/pages/detail/hooks';
import { viewer3dSelectors } from '@viewer3D/redux';
import type { BoundingBoxWithHeight } from '@viewer3D/types';
import { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSnapshot } from 'valtio';
import {
  getClustersAndSinglesForViewport,
  getFilteredPhotosByBbox,
  getFilteredPhotosByDateRange,
} from './helper';
import {
  photosState,
  setAllPhotos,
  setPhotoCategories,
  setPossibleScanners,
  setSelectedCategories,
  setSelectedScanners,
  type Snapshot,
} from './state';
import { useGetProjectScanners } from './useGetProjectScanners';

export type PhotoWithClassification = photoApiV1.Photo & {
  classification?: iphotoContentClassificationApiV1.PhotoContentClassification;
};

type ConnectProps = {
  cameraViewport?: BoundingBoxWithHeight;
  photosInViewport: {
    singles: readonly Snapshot<Photo>[];
    clusters: readonly (readonly Snapshot<Photo>[])[];
  };
  showPhotos: boolean;
  handlePhotosForProject: (photos: PhotoWithClassification[]) => void;
  projectId: string;
};

const webScanner = {
  id: '',
  serial: 'Web',
  latestAssetType: deviceManagementApi.AssetType.UNSPECIFIED,
  deliveryModeState: deviceManagementApi.DeliveryModeState.UNSPECIFIED,
  isRecallCandidate: false,
  isInRecall: false,
  canRecordForRegularProjects: false,
  canRecordForMaintenanceProjects: false,
  canRecordForDensificationProjects: false,
  hasFixedTermRent: false,
};

const MAX_PHOTOS_IN_VIEWPORT = 1000;

export const useConnect = ({
  viewport: bboxFilter,
  dateRange,
}: Pick<ScanFilters, 'dateRange' | 'viewport'>): ConnectProps => {
  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const {
    showPhotos,
    selectedCategories,
    selectedClassifications,
    selectedScanners,
    clusters,
    singles,
    allPhotos,
  } = useSnapshot(photosState);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId) || '';
  const { scanners } = useGetProjectScanners();

  const viewport = cameraViewport ?? bboxFilter;

  useEffect(() => {
    if (showPhotos && dateRange) {
      const filteredPhotos = getFilteredPhotosByDateRange(photosState.allPhotos, dateRange);
      photosState.filteredPhotosByDateOrBbox = filteredPhotos;
    }
    if (showPhotos && bboxFilter) {
      const filteredPhotos = getFilteredPhotosByBbox(photosState.allPhotos, bboxFilter);
      photosState.filteredPhotosByDateOrBbox = filteredPhotos;
    }
  }, [bboxFilter, dateRange, allPhotos.length, showPhotos]);

  useEffect(() => {
    if (viewport && allPhotos.length > 0 && showPhotos) {
      const photosToFilter = photosState.filteredPhotosByDateOrBbox;
      const { singles: viewportSingles, clusters: viewportClusters } =
        getClustersAndSinglesForViewport(
          photosToFilter,
          viewport,
          selectedCategories,
          selectedClassifications,
          selectedScanners,
        );

      if (viewportSingles.length + viewportClusters.length > MAX_PHOTOS_IN_VIEWPORT) {
        errorBox('Too many photos, try a smaller date range or a smaller viewport');
        photosState.showPhotos = false;
        return;
      }
      photosState.singles = viewportSingles;
      photosState.clusters = viewportClusters;
    }
  }, [
    viewport,
    selectedCategories,
    selectedScanners,
    selectedClassifications,
    allPhotos.length,
    showPhotos,
    dateRange,
  ]);

  const photosInViewport = {
    clusters,
    singles,
  };

  const handlePhotosForProject = useCallback(
    (photos: PhotoWithClassification[]) => {
      const allCategories = photos
        .filter((p) => p.categoryName)
        .map((photo) => photo.categoryName) as string[];
      setPhotoCategories([...new Set(allCategories)]);
      setSelectedCategories([...new Set(allCategories)]);
      setPossibleScanners([...scanners, webScanner]);
      setSelectedScanners([...scanners, webScanner]);
      setAllPhotos(
        photos.map((p) => ({
          id: p.id,
          address: p.location?.place ?? null,
          comment: p.comments.at(0)?.text ?? null,
          deviceId: p.deviceId,
          geoPositionSolution: String(
            photoApiV1.GeolocationSolution[p.geoPositionSolution],
          ) as GeoPositionSolution,
          lat: p.location?.coordinate?.latitude ?? 0,
          long: p.location?.coordinate?.longitude ?? 0,
          projectId: p.projectId,
          url: '',
          category: { id: null, name: p.categoryName },
          orderNumbers: [],
          createdAt: timestampToIsoString(p.createdAt),
          recordedAt: timestampToIsoString(p.createdAt),
          updatedAt: timestampToIsoString(p.createdAt),
          classification: p.classification,
        })),
      );
    },
    [scanners],
  );

  return {
    showPhotos,
    cameraViewport,
    photosInViewport,
    projectId,
    handlePhotosForProject,
  };
};
